import type {
  TAssistantOpName,
  TMenuOpName,
} from '@/interfaces/assistant.interface';
import type { IGpthelpTool, IGpthelpToolGate } from '../tool-types';
import { MENU_OP_LIMITS } from '../proposal-op-args';
import { kindOps, proposalToolOffered } from '../proposal-kinds';
import { BOTH_TOOLS_SENTENCE, createProposalTool } from '../proposal-tool';
import type { TProposalText } from '../proposal-tool';
import { switchedOff } from '../tool-switch';

/** The description's first sentences (plan §2): the global rules only. */
const DESCRIPTION_RULES = [
  'Prepares menu changes that the user reviews and applies with one tap on the card; nothing changes until then, and applied changes can be undone for 7 days.',
  `Call it ONCE per request with every change (max ${MENU_OP_LIMITS.changes} changes); take ids from the menu_* read tools, never guess.`,
  "Each change has 'op' plus the fields listed for that op in 'op'.",
  "Discounted prices: when a product or size has a discountedPrice, customers pay that one, so changing its price needs the user's decision about the discount.",
  'Unless the user already said what happens to it, send the changes without discountMode: the answer lists those products with what customers pay now and their new price. Then ask ONE short question in plain words, never field names, that names each of them with both prices, and call again with every change and discountMode.',
];

/** The description's last sentence: the card says it all. */
const DESCRIPTION_CARD =
  'The card shows the changes: never list them, and never say something changed before the user applies it.';

/**
 * The tool description after Phase 0 (plan §2): the global rules only; the
 * fields of each op are in the `op` property (plan F11).
 */
const DESCRIPTION = [
  ...DESCRIPTION_RULES,
  'Never delete products or categories: offer to hide them.',
  DESCRIPTION_CARD,
].join(' ');

/**
 * The tool description while option groups can be created and unlinked
 * (plan P3-5; wording of the P3 gate, W18b A8): a change to a group's
 * choices or rules is shared, a copy serves only some products, links change
 * only their products, a choice for a kind of product goes into the group
 * they share, and a group is removed from its products instead of deleted.
 */
const GROUPS_DESCRIPTION = [
  ...DESCRIPTION_RULES,
  'Option groups are shared: a change to the choices or rules of a group shows in every product that uses it and the card lists them, so prepare it right away; only when the user named some of those products for that change, first ask ONE question: change it for all of them, or use a copy only for those. Adding a group to products or removing it from them changes only those products. To add a choice to every product of a kind («todos los cafés»), add it right away to the group those products share, without asking which products count: the card lists every product it reaches.',
  'Never delete products, categories or option groups: offer to hide them, or to remove a group from its products.',
  DESCRIPTION_CARD,
].join(' ');

/** The ops of option groups and their choices (plan P3-1). */
const GROUP_OPS: readonly TAssistantOpName[] = [
  'createOptionGroup',
  'updateOptionGroup',
  'linkOptionGroup',
  'unlinkOptionGroup',
  'reorderOptionGroups',
  'addOptionChoices',
  'updateOptionChoice',
  'removeOptionChoices',
  'reorderOptionChoices',
];

/**
 * The description for the member's gate: the option-group sentences only
 * while createOptionGroup and unlinkOptionGroup are both offered (plan P3-5:
 * a copy needs both), then the «both tools» sentence only while the
 * inventory's write tool is offered too (plan P5-3, F10); the static
 * definition has neither.
 */
function descriptionFor(gate: IGpthelpToolGate | null): string {
  if (gate === null) return DESCRIPTION;
  const ops = enumOps(gate);
  const description =
    ops.includes('createOptionGroup') && ops.includes('unlinkOptionGroup')
      ? GROUPS_DESCRIPTION
      : DESCRIPTION;
  return proposalToolOffered('stock.changes', gate)
    ? `${description} ${BOTH_TOOLS_SENTENCE}`
    : description;
}

/**
 * stock_getItemCosts is offered to the member: its own `isAvailable`
 * (`stock-tools.ts`) and not switched off (plan F10 naming rule).
 */
function itemCostsOffered(gate: IGpthelpToolGate): boolean {
  return (
    gate.hasRestaurantScope &&
    gate.canManageCatalog &&
    !switchedOff('stock_getItemCosts', gate)
  );
}

/**
 * menu_getItem is offered to the member: its own `isAvailable`
 * (`menu-tools.ts`) and not switched off (plan F10 naming rule).
 */
function itemReadOffered(gate: IGpthelpToolGate): boolean {
  return gate.hasRestaurantScope && !switchedOff('menu_getItem', gate);
}

/**
 * menu_getOptionGroups is offered to the member: its own `isAvailable`
 * (`menu-tools.ts`) and not switched off (plan F10 naming rule).
 */
function optionGroupsReadOffered(gate: IGpthelpToolGate): boolean {
  return (
    gate.hasRestaurantScope &&
    gate.canManageCatalog &&
    !switchedOff('menu_getOptionGroups', gate)
  );
}

/**
 * What follows the op lines while an option-group op is offered (plan
 * P3-5): where group ids come from (the library read only while it is
 * offered, else a product's details), and what a choice price means.
 */
function groupsTrailer(
  ops: readonly TAssistantOpName[],
  gate: IGpthelpToolGate | null
): string | null {
  if (!ops.some((op) => GROUP_OPS.includes(op))) return null;
  const ids =
    gate && optionGroupsReadOffered(gate)
      ? 'Groups: read them first with menu_getOptionGroups (ids, choices and the products that use them); never guess ids.'
      : "Groups: take their ids from a product's details; never guess ids.";
  return `${ids} Choice prices are surcharges (0 = no charge). Product sizes are not groups.`;
}

/**
 * The createOptionGroup line's way to copy a shared group for some of its
 * products, named only while unlinkOptionGroup is offered (plan P3-5, F10
 * naming rule).
 */
const GROUP_COPY_HINT =
  " A copy for only some products = createOptionGroup with copyOf = that group's id and itemIds = those products (send choices only to change some, then every choice with its current price, discount, description and available), plus unlinkOptionGroup of the old group from them, in this same call. Products whose own rules differ need a copy each (the refusal lists them).";

/**
 * The setCategoryAvailability line's pointer to hiding a category's products
 * instead, named only while setAvailability is offered (plan P2-5, F10
 * naming rule).
 */
const KEEP_CATEGORY_HINT =
  ' To hide every product but keep the category, use setAvailability with categoryIds.';

/**
 * createCategory is among the definition's ops (the static definition has
 * every op): a change can point at a category this call creates, and
 * createItem has its Phase 2 fields (`fieldSchemasOf`, W13 decision).
 */
function createsCategories(gate: IGpthelpToolGate | null): boolean {
  return gate === null || enumOps(gate).includes('createCategory');
}

/** Where a product goes (moveItems): also a category this call creates while one can be created. */
function categoryTarget(gate: IGpthelpToolGate | null): string {
  return createsCategories(gate) ? 'categoryId or categoryRef' : 'categoryId';
}

/** The fields of each op, in the `op` property's description (plan §2 P0, §3 P1, §4 P2-5). */
const OP_DOCS: Readonly<Record<TMenuOpName, TProposalText>> = {
  setAvailability: () =>
    "setAvailability: itemIds or categoryIds (= every product of those categories), available (false = sold out/hide), branchScope ('current' = this branch; it also turns on here a product that is off in every branch, keeping it off elsewhere; 'all' = every branch).",
  setPrice: () =>
    "setPrice: itemId, price, sizeId (products with sizes), discountMode ('keep'|'remove'; the user's decision about its discounted price).",
  adjustPrices: () =>
    "adjustPrices: itemIds, categoryIds or all:true; percent (10 = +10 %) or amount (negative lowers); roundTo? (rounds up when raising, down when lowering); discountMode ('scale' = discounted prices move too | 'keep' | 'remove'; the user's decision about discounted prices). Plus plan.",
  renameItem: () => 'renameItem: itemId, name.',
  setDescription: () => "setDescription: itemId, description ('' clears).",
  // The Phase 2 line while the Phase 2 ops are offered; else Phase 1's (W13
  // decision: the dark launch, or createCategory switched off).
  createItem: (gate) =>
    createsCategories(gate)
      ? `createItem: categoryId or categoryRef, name, price, description?, discountedPrice?, available? (false = created hidden; needs a user who manages every branch). Max ${MENU_OP_LIMITS.creates} per call.`
      : `createItem: categoryId, name, price, description? (max ${MENU_OP_LIMITS.creates} per call).`,
  setDiscount: () =>
    'setDiscount: itemId, sizeId (sizes), discountedPrice (what customers pay, below the regular price; null removes it).',
  // Offered on Plus only (`kindOps`); the costs read only while it is offered.
  setPrices: (gate) =>
    `setPrices: prices [{itemId, sizeId?, price?, discountedPrice?}] (up to ${
      MENU_OP_LIMITS.prices
    } different prices, e.g. cost + margin: compute every price yourself), discountMode?${
      gate && itemCostsOffered(gate)
        ? ' Read costs with stock_getItemCosts.'
        : ''
    }`,
  createCategory: () =>
    "createCategory: name, description? (≤255), menuId (required when the store has several menus), available? (false = created hidden; needs a user who manages every branch), ref? (a short id you choose, e.g. 'c1'; other changes of this call use it as categoryRef).",
  renameCategory: () => 'renameCategory: categoryId, name.',
  setCategoryDescription: () =>
    "setCategoryDescription: categoryId, description ('' clears).",
  setCategoryAvailability: (gate) =>
    `setCategoryAvailability: categoryIds, available, branchScope — the category's own switch (products added later follow it).${
      gate && enumOps(gate).includes('setAvailability')
        ? KEEP_CATEGORY_HINT
        : ''
    }`,
  setCategoryPosOnly: () =>
    'setCategoryPosOnly: categoryIds, posOnly (true = only in the Point of Sale, never on the online menu).',
  moveItems: (gate) =>
    `moveItems: itemIds, ${categoryTarget(gate)} (same menu).`,
  reorderItems: () =>
    "reorderItems: categoryId, itemIds, placement? ('first' default | 'last'; the rest keep their order).",
  reorderCategories: () =>
    'reorderCategories: categoryIds (one menu), menuId?, placement?.',
  createOptionGroup: (gate) =>
    `createOptionGroup: name, template ('chooseOne' pick 1, required; 'chooseMany' pick several; 'pricedExtras' paid extras; 'removeIngredients' things to leave out), choices [{name, price?, discountedPrice?, description?, available? (false = paused)}], itemIds? (products that get it), placement? ('last' default | 'first'), required?, min?, max?, description?, copyOf? (a group to copy: its name, rules and choices are used unless you send them).${
      gate && enumOps(gate).includes('unlinkOptionGroup') ? GROUP_COPY_HINT : ''
    }`,
  updateOptionGroup: () =>
    'updateOptionGroup: optionGroupId, name?, description?, required?, multiple?, min?, max? (rules apply to every product using it).',
  linkOptionGroup: () =>
    "linkOptionGroup: optionGroupId, itemIds, placement? ('last' default | 'first').",
  unlinkOptionGroup: () =>
    'unlinkOptionGroup: optionGroupId, itemIds (the group itself stays).',
  reorderOptionGroups: () =>
    'reorderOptionGroups: itemId, optionGroupIds (these first, in order).',
  addOptionChoices: () =>
    'addOptionChoices: optionGroupId, choices [{name, price?, discountedPrice?, description?, available?}].',
  updateOptionChoice: () =>
    "updateOptionChoice: optionGroupId, choiceId, name?, description?, price?, discountedPrice? (null removes), discountMode? ('keep'|'remove'; required when the choice has a discounted price), available? (false = paused, e.g. sold out).",
  removeOptionChoices: () =>
    'removeOptionChoices: optionGroupId, choiceIds (one active choice must stay).',
  reorderOptionChoices: () => 'reorderOptionChoices: optionGroupId, choiceIds.',
  // Phase 4 (plan P4-5), one line per op: the plan's listing puts some on
  // one line, and «Plus plan.» closes both schedule lines.
  addSizes: () =>
    "addSizes: itemId, sizes [{name, price, discountedPrice?}] (a product with one price switches to variants; its price becomes the first one's), discountMode? (a product with a discounted price: ask; 'keep' = the first variant keeps that discount, 'remove' = no discount).",
  updateSize: () =>
    'updateSize: itemId, sizeId, name?, available? (false = paused).',
  // A replacement and the way to reprice a variant that stays name addSizes
  // and setPrice only while each is offered (plan F10 naming rule; W21 B2:
  // removing and adding a variant again would skip the discount question).
  removeSizes: (gate) =>
    `removeSizes: itemId, sizeIds? (without sizeIds every variant goes: back to one price${
      gate && enumOps(gate).includes('addSizes')
        ? ', or replaced by the new ones of an addSizes'
        : ''
    }), price? (default: the first variant's, with its discount), discountedPrice?, discountMode? (a new price when the first variant has a discounted price: ask; 'keep' | 'remove'). To reprice a variant that stays, ${
      gate && enumOps(gate).includes('setPrice')
        ? 'use setPrice with its sizeId; never'
        : 'never'
    } remove it and add it again.`,
  reorderSizes: () => 'reorderSizes: itemId, sizeIds.',
  setItemSchedule: () =>
    "setItemSchedule: itemIds, hourRanges [{day 0=Sunday…6, from 'HH:MM', to 'HH:MM'}] or null (always). Split overnight hours into two days (20:00-23:59 and 00:00-02:00). Plus plan.",
  setCategorySchedule: () =>
    "setCategorySchedule: categoryIds, hourRanges (the category's own hours). Plus plan.",
  // The copy is made before the proposal's other writes (plan F8).
  duplicateItem: () =>
    "duplicateItem: itemId, name (the copy starts hidden in every branch; it is the product as it is now, without this proposal's other changes to it).",
  // The notes are replaced whole; where the current ones are read is named
  // only while that read is offered (plan F10 naming rule).
  setPreparationNotes: (gate) =>
    `setPreparationNotes: itemId, notes (kitchen notes, not the stock recipe) = the full new text (it replaces the current notes; to add to them, include the current ones${
      gate && itemReadOffered(gate) ? ' from menu_getItem' : ''
    }).`,
};

/**
 * What the model reads when a price change needs the member's decision about
 * a discounted price and no way to set a new discounted price is offered
 * (plan P0-2, D2): one plain question, then the call again with
 * `discountMode`. Names no op or tool (plan F10).
 */
const DISCOUNT_DECISION_NOTE =
  'Customers now pay the discounted price of these products, not the regular one (discounts: price = regular, discountedPrice = what they pay, newPrice = the new regular price). Ask the user ONE short question in plain words, in their language, that names each product, what customers pay now and the new price, e.g. "La Medialuna está con descuento a $ 700 (precio normal $ 900). ¿Sus clientes pagan el nuevo precio de $ 1.000, o solo cambia el precio normal y siguen pagando $ 700?". When the change at opIndex is a percent or amount change, ask instead whether the discounted prices change too, stay as they are, or are removed. When newPrice is not above discountedPrice the discount cannot stay: ask only whether to remove it. Never mention field names. Then call again with every change and discountMode: "remove" = they pay the new price; "keep" = they keep paying the discounted price; "scale" (percent or amount changes only) = the discounted prices change too. Prepare nothing else now.';

/**
 * Why a product was left out as DISCOUNT_INVALID when no way to set a new
 * discounted price is offered (plan P0-6). Names no op or tool (plan F10).
 */
const DISCOUNT_INVALID_WHY =
  'its discounted price would not stay below the new price: ask whether to remove the discount';

/** A way to give a new discounted price, named only while its op is offered. */
interface INewDiscountWay {
  op: TAssistantOpName;
  /** How the decision note names it. */
  note: string;
  /** How the DISCOUNT_INVALID why names it. */
  why: string;
}

/**
 * The ways to give a new discounted price (plan P1-1, P3-2, P4-1, X1), each
 * named only while its op is offered.
 */
const NEW_DISCOUNT_WAYS: readonly INewDiscountWay[] = [
  {
    op: 'setDiscount',
    note: 'setDiscount for a product or variant',
    why: 'setDiscount',
  },
  {
    op: 'updateOptionChoice',
    note: 'discountedPrice on updateOptionChoice for an option choice',
    why: 'discountedPrice on updateOptionChoice for a choice',
  },
  {
    op: 'addSizes',
    note:
      'for a product switching to variants, discountedPrice on the first addSizes entry',
    why: 'discountedPrice on the first addSizes entry',
  },
  {
    op: 'removeSizes',
    note: 'back to one price, discountedPrice on removeSizes',
    why: 'discountedPrice on removeSizes',
  },
];

/** The ways to give a new discounted price among `ops`. */
function offeredWays(ops: readonly string[]): INewDiscountWay[] {
  return NEW_DISCOUNT_WAYS.filter((way) => ops.includes(way.op));
}

/**
 * What the model reads when a price change needs the member's decision about
 * a discounted price (plan P1-1, D2 answers): the P0 text while no way to set
 * a new discounted price is offered; else the member may also answer with a
 * new discounted price, through the ways offered (plan F10 naming rule).
 */
export function discountDecisionNote(ops: readonly string[]): string {
  const ways = offeredWays(ops);
  if (ways.length === 0) return DISCOUNT_DECISION_NOTE;
  return (
    'Customers now pay the discounted price of these products, not the regular one (discounts: price = regular, discountedPrice = what they pay, newPrice = the new regular price). ' +
    'Ask the user ONE short question in plain words, in their language, that names each product, what customers pay now and the new price, e.g. ' +
    '"La Medialuna está con descuento a $ 700 (precio normal $ 900). ¿Sus clientes pagan el nuevo precio de $ 1.000, solo cambia el precio normal y siguen pagando $ 700, o quiere poner otro precio con descuento?". ' +
    'When the change at opIndex is a percent or amount change, ask instead whether the discounted prices change too, stay as they are, or are removed. ' +
    'When newPrice is not above discountedPrice the discount cannot stay: ask whether to remove it or set a new discounted price. Never mention field names. ' +
    'Then call again with every change and discountMode: "remove" = they pay the new price; "keep" = they keep paying the discounted price; "scale" (percent or amount changes only) = the discounted prices change too; ' +
    `or a new discounted price (${ways
      .map((way) => way.note)
      .join('; ')}) in the same call. Prepare nothing else now.`
  );
}

/**
 * Why a product was left out as DISCOUNT_INVALID (plan P1-1): the P0 text
 * while no way to set a new discounted price is offered; else it also offers
 * a new one, through the ways offered (plan F10 naming rule).
 */
export function discountInvalidWhy(ops: readonly string[]): string {
  const ways = offeredWays(ops);
  if (ways.length === 0) return DISCOUNT_INVALID_WHY;
  return `${DISCOUNT_INVALID_WHY} or set a new one (${ways
    .map((way) => way.why)
    .join(', or ')})`;
}

/**
 * Why a new product was left out as DISCOUNT_INVALID: it is not created, and
 * no op can change a product that does not exist yet. Asked again with a
 * lower discounted price or without one; createItem is named only while it
 * is offered (plan F10 naming rule; it is whenever this can happen).
 */
export function discountInvalidCreateWhy(ops: readonly string[]): string {
  const why =
    'the discounted price is not below the price: ask for a lower discounted price, or create it without one';
  return ops.includes('createItem')
    ? `${why} (send the createItem again)`
    : why;
}

/**
 * The ops `menu_proposeChanges` offers to this member (plan X4): every menu
 * op but those switched off (killed, or still dark for them) and setPrices
 * below Plus.
 */
export function enumOps(
  gate: Pick<IGpthelpToolGate, 'assistantDisabledOps' | 'effectiveTier'>
): TAssistantOpName[] {
  return kindOps('menu.changes', gate);
}

/**
 * `menu_proposeChanges` (plan §9.7, X4, Y1): prepares an inert menu proposal
 * through the Action API; the member applies it from the card. Offered while
 * writes are on for the store, to members who may change the menu or turn
 * products on and off, with the ops not switched off for them.
 */
export const menuProposeTool: IGpthelpTool = createProposalTool({
  kind: 'menu.changes',
  description: descriptionFor,
  opDocs: OP_DOCS,
  opTrailer: groupsTrailer,
  discountTexts: {
    decisionNote: discountDecisionNote,
    invalidWhy: discountInvalidWhy,
    invalidCreateWhy: discountInvalidCreateWhy,
  },
});
